import React from "react"
import Layout from "../../../components/Layout"
import PageHero from "../../../components/PageHero"
import News from "../../../components/News"
import "../../../styles/news.scss"
import "../../../styles/caseStudy.scss"
import heroImg from "../../../images/caseStudies/nurturefulth.png"
import clientLogo from "../../../images/clients/Colored/nurtureful_logo.png"

const CHALLENGES = [
  {
    title: "Fragmented Patient Onboarding",
    desc: "Manual intake processes and paper-based forms led to delays in starting therapy and created a heavy administrative burden on clinicians.",
  },
  {
    title: "Manual Booking Inefficiencies",
    desc: "Without a centralized scheduling system, the practice faced high overhead in managing appointments, leading to occasional double-bookings and scheduling friction.",
  },
  {
    title: "Telehealth Fragmentation",
    desc: "Using disconnected third-party video tools made it difficult to maintain a seamless, professional experience for remote clients while ensuring strict data privacy.",
  },
  {
    title: "Billing & Payment Friction",
    desc: "Inconsistent invoicing and manual payment tracking resulted in delayed collections and a lack of transparency for patients regarding their session history.",
  },
]

const SOLUTIONS = [
  { num: "01", text: "Custom High-Performance Website", desc: "A secure, responsive, and welcoming web presence that reflects the practice's values while serving as the primary gateway for new and returning patients." },
  { num: "02", text: "Intelligent Booking Engine", desc: "A real-time scheduling system that allows patients to view availability, book sessions instantly, and manage their own appointments without administrative intervention." },
  { num: "03", text: "Integrated Telehealth Suite", desc: "A secure, end-to-end encrypted video session platform built directly into the workflow, providing a stable and professional environment for virtual care." },
  { num: "04", text: "Patient Self-Service Portal", desc: "A dedicated hub where clients can securely access session history, download resources, update personal information, and communicate with the practice." },
  { num: "05", text: "Automated Billing & Payments", desc: "Seamless integration of payment gateways to handle session fees, automated invoicing, and digital receipts, ensuring a frictionless financial experience." },
  { num: "06", text: "Clinical Practice Analytics", desc: "A real-time dashboard for leadership to track session volumes, patient retention, and revenue growth to inform strategic practice decisions." },
]

const RESULTS = [
  { metric: "50%", label: "Reduction in administrative time spent on scheduling and manual patient onboarding" },
  { metric: "35%", label: "Decrease in session no-shows through automated multi-channel appointment reminders" },
  { metric: "100%", label: "Secure, HIPAA-aligned digital workflow ensuring complete patient confidentiality across all online touchpoints" },
]

const RESULT_BULLETS = [
  "Enhanced Patient Satisfaction with 24/7 access to booking and a seamless transition from session to payment",
  "Improved Clinician Focus by automating routine tasks, allowing therapists to dedicate more time to clinical care",
]

const NurturefulPsychology = () => (
  <div className="CaseStudeies">
    <Layout
      pageTitle="Visiomate - Nurtureful Psychology Case Study"
      descrip="End-to-End Digital Transformation for Nurtureful Psychology — custom website, booking engine, telehealth suite, and patient portal built by Visiomate."
    >
      <PageHero
        eyebrow="Customer Story"
        title="Nurtureful Psychology"
        description="End-to-end digital transformation for a premier mental health practice"
      />

      {/* About */}
      <section className="vm-news-section vm-news-section--light">
        <div className="vm-news-inner cs-intro">
          <div className="cs-intro__img">
            <img src={heroImg} alt="Nurtureful Psychology" />
          </div>
          <div className="cs-intro__text">
            <img src={clientLogo} alt="Nurtureful Psychology logo" className="cs-client-logo" />
            <h2 className="vm-news-heading vm-news-heading--dark">
              End-to-End Digital Transformation for Nurtureful Psychology
            </h2>
            <p className="vm-news-sub vm-news-sub--dark">
              Nurtureful Psychology is a premier mental health practice dedicated to providing
              accessible, compassionate, and professional psychological support. Specializing in
              individual therapy and specialized assessments, they empower clients to navigate life's
              challenges through evidence-based therapeutic approaches. By prioritizing a
              "person-first" methodology, they offer a welcoming space for healing — both in person
              and across the digital landscape.
            </p>
          </div>
        </div>
      </section>

      {/* Challenges */}
      <section className="vm-news-section vm-news-section--gray">
        <div className="vm-news-inner">
          <div className="vm-news-header">
            <h2 className="vm-news-heading vm-news-heading--dark">What They Were Up Against</h2>
            <p className="vm-news-sub vm-news-sub--dark">
              Before working with Visiomate, Nurtureful faced four critical operational gaps.
            </p>
          </div>
          <div className="cs-challenge-grid">
            {CHALLENGES.map(c => (
              <div key={c.title} className="cs-challenge-card">
                <div className="cs-challenge-card__icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                </div>
                <h3 className="cs-challenge-card__title">{c.title}</h3>
                <p className="cs-challenge-card__desc">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solution */}
      <section className="vm-news-section vm-news-section--light">
        <div className="vm-news-inner">
          <div className="vm-news-header">
            <h2 className="vm-news-heading vm-news-heading--dark">What We Built</h2>
            <p className="vm-news-sub vm-news-sub--dark">
              We designed and implemented a comprehensive digital ecosystem tailored to the sensitive
              and specialized needs of a clinical psychology practice.
            </p>
          </div>
          <div className="cs-solution-layout">
            <div className="cs-solutions-grid">
              {SOLUTIONS.map(s => (
                <div key={s.num} className="cs-solution-item">
                  <div className="cs-solution-item__badge">{s.num}</div>
                  <div>
                    <span className="cs-solution-item__text">{s.text}</span>
                    <p className="cs-solution-item__desc">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="cs-solution-img">
              <img src={heroImg} alt="Nurtureful Psychology" />
            </div>
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="vm-news-section vm-news-section--gray">
        <div className="vm-news-inner">
          <div className="vm-news-header">
            <h2 className="vm-news-heading vm-news-heading--dark">A Dramatic Transformation</h2>
            <p className="vm-news-sub vm-news-sub--dark">
              Nurtureful Psychology saw measurable impact across every part of the practice.
            </p>
          </div>
          <div className="cs-results-grid">
            {RESULTS.map(r => (
              <div key={r.metric} className="cs-result-card">
                <span className="cs-result-card__metric">{r.metric}</span>
                <p className="cs-result-card__label">{r.label}</p>
              </div>
            ))}
            {RESULT_BULLETS.map(b => (
              <div key={b} className="cs-result-card cs-result-card--bullet">
                <div className="cs-result-card__icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <p className="cs-result-card__label">{b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Conclusion */}
      {/* <section className="vm-news-section vm-news-section--dark">
        <div className="vm-news-inner cs-conclusion">
          <h2 className="vm-news-heading">The Outcome</h2>
          <p className="vm-news-sub">
            By unifying their clinical workflow into one seamless digital system, Nurtureful Psychology
            achieved operational excellence, greater professional transparency, and a superior patient
            experience. What was once a fragmented, manual process is now a streamlined, data-driven
            practice — positioning Nurtureful Psychology as a modern, patient-centric leader in the
            mental health industry.
          </p>
        </div>
      </section> */}

      <News isAbout={true} />
    </Layout>
  </div>
)

export default NurturefulPsychology
